import { useApiMutation, useApiQuery } from "../services/react-query";

export const useGetInvites = ({ eventId, ...query }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["invites", eventId, query],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/events/${eventId}/invites`,
      method: "GET",
      params: query,
    },
  });

export const useGetInviteStats = (eventId) =>
  useApiQuery({
    queryOptions: {
      // Prefixed with "invites" so the same invalidateQueryKeys used by the
      // mutations (import/send/CRUD) also refreshes the stats.
      queryKey: ["invites", "stats", eventId],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/events/${eventId}/invites/stats`,
      method: "GET",
    },
  });

export const useImportInvites = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useImportInvites" },
    fetchOptions: {
      url: `/events/${eventId}/invites/import`,
      method: "POST",
    },
    invalidateQueryKeys: [["invites"]],
  });

export const useAddInviteRecipient = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useAddInviteRecipient" },
    fetchOptions: {
      url: `/events/${eventId}/invites/recipient`,
      method: "POST",
    },
    invalidateQueryKeys: [["invites"]],
    successMessage: { title: "Recipient added", color: "green" },
  });

export const useUpdateInviteRecipient = ({ eventId, id }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateInviteRecipient" },
    fetchOptions: {
      url: `/events/${eventId}/invites/recipient/${id}`,
      method: "PATCH",
    },
    invalidateQueryKeys: [["invites"]],
    successMessage: { title: "Recipient updated", color: "green" },
  });

// Delete takes the id in the body at mutate time so a single hook works for
// every row (see useDeleteConfirm Pattern A: confirmDelete({ id })).
export const useDeleteInviteRecipient = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteInviteRecipient" },
    fetchOptions: {
      url: `/events/${eventId}/invites/recipient`,
      method: "DELETE",
    },
    invalidateQueryKeys: [["invites"]],
    successMessage: { title: "Recipient removed", color: "green" },
  });

export const useGetInviteCampaign = (eventId) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["inviteCampaign", eventId],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/events/${eventId}/invites/campaign`,
      method: "GET",
    },
  });

export const useSaveInviteCampaign = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useSaveInviteCampaign" },
    fetchOptions: {
      url: `/events/${eventId}/invites/campaign`,
      method: "PUT",
    },
    invalidateQueryKeys: [["inviteCampaign"]],
    successMessage: { title: "Invite email saved", color: "green" },
  });

// Sending is queued, not immediate: the request claims the recipients and hands
// them to background jobs, so the response confirms queueing and the table shows
// rows advancing Queued -> Sent as the jobs run.
export const useSendInvites = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useSendInvites" },
    fetchOptions: {
      url: `/events/${eventId}/invites/send`,
      method: "POST",
    },
    invalidateQueryKeys: [["invites"]],
    successMessage: {
      title: "Invites queued",
      message: "They are being sent in the background.",
      color: "green",
    },
  });

export const useResendInvites = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useResendInvites" },
    fetchOptions: {
      url: `/events/${eventId}/invites/resend`,
      method: "POST",
    },
    invalidateQueryKeys: [["invites"]],
    hideNotification: true,
  });
